import {
    equalTo,
    get,
    getDatabase,
    orderByChild,
    push,
    query,
    ref,
    remove,
    set,
    update,
} from 'firebase/database'
import { v4 } from 'uuid'
import { Res } from './types'
import {
    ECCAccreditationLevel,
    EducatorProfile,
    EmploymentSetting,
    FieldState,
    SupportStaffProfile,
    TeacherAccreditationChangeRequest,
    TeacherRequest,
    TeacherRequestTypes,
    TeacherWorkingHistoryEntry,
} from './types-teacher'
import {
    ConfirmedPlacementState,
    TeacherAccountState,
    TeacherAvailability,
    TeacherAvailabilityStateForDate,
    TeacherPlacement,
    TeacherPlacementKeys,
    TeacherProfile,
    TeacherPlacementState,
} from './types-teacher'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import moment from 'moment'
import { PlaceType } from '../components/AutoComplete'
import { updateProfile, User } from 'firebase/auth'
import {
    AccountType,
    AccreditationDocument,
    AccreditationDocument_v2,
    LocalTeacherEmploymentType,
    TeacherEmploymentSetting,
    TeacherFields,
} from '../redux/types'
import { v4 as uuidv4 } from 'uuid'
import { db, functions } from './configs'
import { logger } from '../helpers/logger'
import { UserLocationType } from '../schemas/teacherReg'
import { VicDeclarationForm } from '../pages/Dashboard/ReviewPage/components/DeclarationForm'
import { httpsCallable } from 'firebase/functions'
import {
    SubmitEmploymentScreeningConsentFormArgs,
    SuccessCallableResponse,
} from './schema'

dayjs.extend(customParseFormat)

export const TEACHER_API = {
    get: async <T>(
        teacherUid: string,
        name: keyof TeacherFields
    ): Promise<T | null> =>
        (await get(ref(db, `teachers/${teacherUid}/${name}/value`))).val(),
    update: (teacherUid: string, data: Partial<TeacherFields>) =>
        update(ref(db, `teachers/${teacherUid}`), data),
    updateChild: (
        teacherUid: string,
        childName: keyof TeacherFields,
        data: any
    ) => update(ref(db, `teachers/${teacherUid}/${childName}`), data),
    setChild: (teacherUid: string, childName: keyof TeacherFields, data: any) =>
        set(ref(db, `teachers/${teacherUid}/${childName}`), data),
    profile: {
        update: (teacherUid: string, data: Partial<TeacherProfile>) =>
            update(ref(db, `teachers/${teacherUid}/profile`), data),
        updateQualifyingArea: (
            teacherUid: string,
            subject: string,
            data: Record<string, boolean>
        ) =>
            update(
                ref(
                    db,
                    `teachers/${teacherUid}/profile/qualifiedTeachingAreas/${subject}`
                ),
                data
            ),
        fetchSubjectsList: () =>
            get(ref(db, `publicResources/qualifiedTeachingAreas`)),
    },
    supportProfile: {
        update: (teacherUid: string, data: Partial<SupportStaffProfile>) =>
            update(ref(db, `teachers/${teacherUid}/supportStaffProfile`), data),
    },
    educatorProfile: {
        update: (teacherUid: string, data: Partial<EducatorProfile>) =>
            update(ref(db, `teachers/${teacherUid}/educatorProfile`), data),
    },
    employmentSetting: {
        get: (teacherUid: string) =>
            get(ref(db, `teachers/${teacherUid}/employmentSetting`)),
    },
    onboarding: {
        submitVicDecForm: (form: VicDeclarationForm) =>
            httpsCallable<
                SubmitEmploymentScreeningConsentFormArgs,
                SuccessCallableResponse
            >(
                functions,
                'submitEmploymentScreeningConsentForm'
            )({
                declarationForm: {
                    ...form,
                    dob: form.dob.format('DD-MM-YYYY'),
                },
                deviceType: 'web',
            }),
        createWorkingHistory: (data: TeacherWorkingHistoryEntry) =>
            httpsCallable(functions, 'createWorkingHistory')(data),
        updateWorkingHistory: (
            historyId: string,
            data: TeacherWorkingHistoryEntry
        ) =>
            httpsCallable(
                functions,
                'updateWorkingHistory'
            )({ historyId, data }),
        removeWorkingHistory: (payload: string | string[]) =>
            httpsCallable(
                functions,
                'removeWorkingHistory'
            )({ historyId: payload }),

        markNoWorkingHistory: (uid: string) =>
            set(ref(db, `teachers/${uid}/workingHistory/state`), 'submitted'),
    },
} as const

export const createTeacherRequest = async (request: TeacherRequest) => {
    try {
        const docRef = ref(
            getDatabase(),
            `adminNotifications/teacherRequest/new/${v4()}`
        )
        await update(docRef, request)
        return { code: 200 }
    } catch (error: any) {
        return {
            code: 500,
            error: error,
            errorMsg: error.code ? error.code : null,
        }
    }
}

export const deleteTeacherAccount = async (
    id: string,
    homeCountry?: string,
    homeState?: string
) => {
    try {
        if (!homeCountry || !homeState) {
            return { code: 200 }
        }
        // MARK: - FETCH TEACHER AVAILABILITY AND MARK DATES AVAILABLE AS UNAVAILABLE (availableTeachers)
        const res = await fetchAvailability(id, homeCountry, homeState)
        if (res.code === 500) {
            throw new Error('Failed to fetch teacher availability')
        }
        for (let [key, value] of Object.entries(res.data)) {
            if (
                (value as TeacherAvailabilityStateForDate) ===
                TeacherAvailabilityStateForDate.available
            ) {
                const res2 = await setAvailabilityForDate(
                    id,
                    homeCountry,
                    homeState,
                    key,
                    TeacherAvailabilityStateForDate.unavailable
                )
                if (res2.code === 500) {
                    throw new Error('Failed to remove teacher availability')
                }

                await new Promise((res, _) => setTimeout(res, 200))
            }
        }
        // MARK: - Set Teacher's individual availability to null
        const removeRes = await removeTeacherAvailability(
            id,
            homeCountry,
            homeState
        )
        if (removeRes.code === 500) {
            throw new Error('Failed to fetch set teacher availability')
        }
        logger.debug('TEACHER_DELETE: Set unavailability complete')
        // MARK: - CANCEL ANY CONFIRMED UPCOMING BOOKINGS
        const placementRes = await fetchPlacementKeys(
            id,
            homeCountry,
            homeState
        )
        if (placementRes.code === 500) {
            throw new Error('Failed to fetch teacher placement keys')
        }
        logger.debug(placementRes.data)
        let date = dayjs()
        for (let [year, p] of Object.entries(placementRes.data)) {
            if (parseInt(year) >= date.year()) {
                for (let [date, placement] of Object.entries(p)) {
                    if (
                        dayjs(date, 'DD-MM-YYYY').isSame(date) ||
                        dayjs(date, 'DD-MM-YYYY').isAfter(date)
                    ) {
                        for (let [pId, pContent] of Object.entries(placement)) {
                            await teacherCancelPlacementForDeleteAccount(
                                id,
                                homeCountry,
                                homeState,
                                pId,
                                pContent
                            )
                            // add delay between calls to avoid cloud functions crash
                            await new Promise((res, _) => setTimeout(res, 200))
                        }
                    }
                }
            }
        }
        logger.debug('TEACHER_DELETE: Cancelled all upcoming placements')
        // MARK: - UPDATE TEACHER ACCOUNT STATE & REMOVE TOKENS & FAVOURITE SCHOOLS
        const teacherRef = ref(getDatabase(), `teachers/${id}`)
        await update(teacherRef, {
            accountState: {
                value: TeacherAccountState.DELETED.valueOf(),
                deletedBy: id,
                deletedOnDate: moment().format('DD-MM-YYYY HH:mm:ss'),
            },
            token: null,
            tokens: null,
            favouriteSchools: null,
        })
        logger.debug(
            'TEACHER_DELETE: Updated Account state, remove tokens and favourite schools'
        )

        return { code: 200 }
    } catch (error: any) {
        logger.error(`${error}`)
        return { code: 500, error: error }
    }
}

export const fetchAvailability = async (
    id: string,
    homeCountry: string,
    homeState: string
): Promise<Res<TeacherAvailability>> => {
    try {
        const docRef = ref(
            getDatabase(),
            `countries/${homeCountry}/${homeState}/teachers/availability/${id}`
        )
        const snap = await get(docRef)
        if (snap.exists()) {
            let availability: TeacherAvailability = {}
            Object.entries(snap.val()).forEach(([key, value]) => {
                availability = {
                    ...availability,
                    [`${key}`]: value as string,
                }
            })
            return { code: 200, data: availability }
        }
        return { code: 200, data: {} as TeacherAvailability }
    } catch (error: any) {
        logger.error('Failed to fetch user availability')
        return {
            code: 500,
            error: error,
            errorMsg: 'Failed to fetch user availability',
        }
    }
}

const getMonthString = (month: number) => {
    if (month <= 8) {
        return `0${month + 1}`
    }
    return `${month + 1}`
}

export const setAvailabilityForDate = async (
    teacherUid: String,
    homeCountry: String,
    homeState: string,
    date: string,
    state: TeacherAvailabilityStateForDate
) => {
    try {
        // Remove availability from availableTeacher node - NOTE(BEWARE): This will trigger a cloud function to reflect changes to relative schools!!
        let d = dayjs(date, 'DD-MM-YYYY')
        const docRef = ref(
            getDatabase(),
            `countries/${homeCountry}/${homeState}/availableTeachers/${d.year()}/${getMonthString(
                d.month()
            )}/${date}/${teacherUid}`
        )
        await update(docRef, {
            state: state.valueOf(),
            radius: 0.0,
        })
        logger.debug(
            `setting availability for ${date}, ${d.year()} ${getMonthString(
                d.month()
            )} to ${state.valueOf()}`
        )
        return { code: 200 }
    } catch (error: any) {
        logger.error(`Failed to set availability for ${teacherUid} at ${date}`)
        return { code: 500, error: error }
    }
}

export const removeTeacherAvailability = async (
    teacherUid: string,
    homeCountry: string,
    homeState: string
) => {
    try {
        // Remove availability from availability/teacher node
        const teacherAvaRef = ref(
            getDatabase(),
            `countries/${homeCountry}/${homeState}/teachers/availability`
        )
        await update(teacherAvaRef, {
            [`${teacherUid}`]: {},
        })
        return { code: 200 }
    } catch (error: any) {
        logger.error(`Failed to reset availability for ${teacherUid}`)
        return { code: 500, error: error }
    }
}

export const fetchPlacementKeys = async (
    teacherUid: string,
    homeCountry: string,
    homeState: string
): Promise<Res<TeacherPlacementKeys>> => {
    try {
        const docRef = ref(
            getDatabase(),
            `countries/${homeCountry}/${homeState}/teachers/placements/${teacherUid}`
        )
        const snap = await get(docRef)
        if (snap.exists()) {
            return { code: 200, data: snap.val() as TeacherPlacementKeys }
        }
        return { code: 200, data: {} }
    } catch (error: any) {
        return { code: 500, error: error }
    }
}

// NOTE(BEWARE): This action will trigger cloud functions!!
export const teacherCancelPlacementForDeleteAccount = async (
    teacherUid: string,
    homeCountry: string,
    homeState: string,
    placementId: string,
    key: TeacherPlacement
) => {
    try {
        logger.debug(`cancelling ${placementId} ${JSON.stringify(key)}`)
        const placementDate = dayjs(key.date, 'DD-MM-YYYY')
        logger.debug(
            `${placementDate.year()}/${getMonthString(placementDate.month())}/${
                key.date
            }/${teacherUid}`
        )

        const docRef = ref(
            getDatabase(),
            `countries/${homeCountry}/${homeState}/placements/confirmed/${placementDate.year()}/${getMonthString(
                placementDate.month()
            )}/${key.date}/${placementId}`
        )
        await update(docRef, {
            'teacherDetails/state': TeacherPlacementState.cancelled.valueOf(),
            'placementDetails/state':
                ConfirmedPlacementState.cancelledByTeacher.valueOf(),
            'placementDetails/teacherCancelTimeStamp': moment().format(
                'DD-MM-YYYY HH:mm:ss'
            ),
        })
        logger.debug(`-- confirmed placement`)

        const avaTeachersRef = ref(
            getDatabase(),
            `countries/${homeCountry}/${homeState}/availableTeachers/${placementDate.year()}/${getMonthString(
                placementDate.month()
            )}/${key.date}/${teacherUid}`
        )
        await update(avaTeachersRef, {
            state: TeacherAvailabilityStateForDate.unavailable.valueOf(),
            [`requestedBy/${key.schoolUid}`]: null,
        })
        logger.debug(`-- availableTeachers`)

        const schoolPlacementsRef = ref(
            getDatabase(),
            `countries/${homeCountry}/${homeState}/schools/placements/${
                key.schoolUid
            }/${placementDate.year()}/${key.date}/${teacherUid}`
        )
        await update(schoolPlacementsRef, {
            cancelled: true,
            cancelledByTeacher: true,
        })
        logger.debug(`-- school placements`)

        const teacherPlacementsRef = ref(
            getDatabase(),
            `countries/${homeCountry}/${homeState}/teachers/placements/${teacherUid}/${placementDate.year()}/${
                key.date
            }/${placementId}`
        )
        await update(teacherPlacementsRef, {
            cancelled: true,
        })
        logger.debug(`-- teacher placements`)

        logger.debug(
            `CANCELLED_PLACEMENT: ${placementId} ${placementDate.format(
                'YYYY-MM-DD'
            )}`
        )

        return { code: 200 }
    } catch (error: any) {
        logger.error('error:', error)
        return { code: 500, error: error }
    }
}

export const setAccountLocation = async (
    userId: string,
    userType: AccountType,
    homeLocation: PlaceType
) => {
    try {
        let address = homeLocation.address_components
        let geometry = homeLocation.geometry
        if (!address || !geometry) return {}
        let country = address.filter((el) => el.types.includes('country'))[0]
            .long_name
        let state = address.filter((el) =>
            el.types.includes('administrative_area_level_1')
        )[0].short_name
        let suburb = address.filter((el) => el.types.includes('locality'))[0]
            .long_name
        let postal_code = address.filter((el) =>
            el.types.includes('postal_code')
        )[0].long_name
        let long_address =
            homeLocation.description ||
            homeLocation.formatted_address ||
            homeLocation.structured_formatting.secondary_text ||
            'No suitable address'

        const userNode = ref(getDatabase(), `users/${userId}`)
        await update(userNode, {
            homeCountry: country,
            homeState: state,
        })
        switch (userType) {
            case 'teacher':
                const teacherHomeLocationNode = ref(
                    getDatabase(),
                    `teachers/${userId}/homeLocation`
                )
                await update(teacherHomeLocationNode, {
                    address: long_address,
                    country: country,
                    homeState: state,
                    lat: geometry.location.lat(),
                    long: geometry.location.lng(),
                    postCode: postal_code,
                    suburb: suburb,
                    state: 'submitted',
                })
                const teacherSetLocationNode = ref(
                    getDatabase(),
                    `teachers/${userId}/setLocation`
                )
                await update(teacherSetLocationNode, {
                    lat: geometry.location.lat(),
                    long: geometry.location.lng(),
                    country: country,
                    homeState: state,
                })
                break
            case 'school':
            case 'childcareCentre':
                break
        }
        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const setAccountLocation_v2 = async (
    userId: string,
    userType: AccountType,
    homeLocation: UserLocationType
) => {
    try {
        const userNode = ref(getDatabase(), `users/${userId}`)
        await update(userNode, {
            homeCountry: homeLocation.country,
            homeState: homeLocation.homeState,
        })
        switch (userType) {
            case 'teacher':
                const teacherHomeLocationNode = ref(
                    getDatabase(),
                    `teachers/${userId}/homeLocation`
                )
                await update(teacherHomeLocationNode, {
                    ...homeLocation,
                    state: 'submitted',
                })
                const teacherSetLocationNode = ref(
                    getDatabase(),
                    `teachers/${userId}/setLocation`
                )
                await update(teacherSetLocationNode, {
                    lat: homeLocation.lat,
                    long: homeLocation.long,
                    country: homeLocation.country,
                    homeState: homeLocation.homeState,
                })
                break
            case 'school':
            case 'childcareCentre':
                break
        }
        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const updateTeacherContact = async (userId: string, contact: string) => {
    try {
        const userNode = ref(getDatabase(), `users/${userId}`)
        await update(userNode, {
            contact: contact,
        })
        const teacherNode = ref(getDatabase(), `teachers/${userId}/contact`)
        await update(teacherNode, {
            value: contact,
        })
        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const updateTeacherProfilePhoto = async (
    user: User,
    photoUrl: string
) => {
    try {
        const userNode = ref(getDatabase(), `users/${user.uid}`)
        await update(userNode, {
            profilePictureURL: photoUrl,
        })
        const teacherNode = ref(
            getDatabase(),
            `teachers/${user.uid}/profilePicture`
        )
        await update(teacherNode, {
            value: photoUrl,
            state: 'submitted',
        })

        await updateProfile(user, { photoURL: photoUrl })

        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const updateEmergencyContact = async (
    userId: string,
    type: 'first' | 'second',
    name: string,
    contact: string,
    relationship: string
) => {
    try {
        const userNode = ref(
            getDatabase(),
            `teachers/${userId}/${type}EmergencyContact`
        )

        await update(userNode, {
            contact,
            name,
            relationship,
        })

        return { code: 200 }
    } catch (error: any) {
        return { code: 500, error: error }
    }
}

export const uploadAccreditationDocument = async (
    userId: string,
    teacherName: string,
    doc: AccreditationDocument_v2
) => {
    try {
        // Delete any rejected document of the same type;
        const teacherAccNode = ref(
            getDatabase(),
            `teachers/${userId}/accreditation`
        )

        const sameTypeDocsSnap = await get(
            query(
                teacherAccNode,
                orderByChild('accreditationType'),
                equalTo(doc.accreditationType)
            )
        )

        // const accSnap = await get(teacherAccNode)
        const dbUpdates: Record<string, any> = {}

        if (sameTypeDocsSnap.exists()) {
            for (let [accId, accDoc] of Object.entries(
                sameTypeDocsSnap.val() as {
                    [id: string]: AccreditationDocument_v2
                }
            )) {
                if ((accDoc.state || 'incomplete') === FieldState.REJECTED) {
                    // Remove rejected documents of the same type
                    dbUpdates[`teachers/${userId}/accreditation/${accId}`] =
                        null
                    // remove(
                    //     ref(
                    //         getDatabase(),
                    //         `teachers/${userId}/accreditation/${accId}`
                    //     )
                    // )
                }
            }
        }

        // const reqId = v4()
        // const newAccRef = push(ref(db, `teachers/${userId}/accreditation`))

        // Add document to teacher accreditation node
        const newAccRef = push(ref(db, `teachers/${userId}/accreditation`))
        const newAccKey = newAccRef.key
        await set(newAccRef, doc)

        // Create admin request
        const req: TeacherAccreditationChangeRequest = {
            state: FieldState.SUBMITTED,
            teacherUid: userId,
            teacherName: teacherName,
            documentUid: newAccKey,
            timeStamp: new Date().getTime() / 1000,
            type: TeacherRequestTypes.ACC_DOC_UPDATE,
            url: doc.imageURL,
        }

        await push(ref(db, `adminNotifications/teacherRequest/new`), req)

        return { code: 200 }
    } catch (error) {
        logger.error(
            error instanceof Error ? error.message : JSON.stringify(error)
        )
        return { code: 500, error: error }
    }
}

export const setTeacherAccountType = async (
    teacherId: string,
    type: LocalTeacherEmploymentType,
    locationState: string
) => {
    try {
        const teacherRef = ref(
            getDatabase(),
            `teachers/${teacherId}/employmentSetting`
        )
        let data: any = empSettingLocaltoFb(locationState, type)

        await set(teacherRef, data)
        return { code: 200 }
    } catch (error: any) {
        return { code: 500, error: error }
    }
}

export const addTeacherEmploymentSetting = async (
    teacherId: string,
    type: TeacherEmploymentSetting,
    locationState: string,
    level?: ECCAccreditationLevel
): Promise<Res<string>> => {
    try {
        const nodeRef = ref(
            getDatabase(),
            `teachers/${teacherId}/employmentSetting`
        )
        const newId = uuidv4()
        await update(nodeRef, {
            [`${newId}`]: {
                locationState,
                type,
                state: TeacherAccountState.INCOMPLETE,
                ...(type === 'earlyChildcare' ? { level: level || null } : {}),
            },
        })

        return { code: 200, data: newId }
    } catch (error: any) {
        return { code: 500, error: error }
    }
}

export const addTeacherEmploymentSetting_v2 = async (
    teacherId: string,
    setting: EmploymentSetting
    // type: TeacherEmploymentSetting,
    // locationState: AusState,
    // level?: EducatorEmploymentLevel
): Promise<Res<[string, EmploymentSetting]>> => {
    try {
        const nodeRef = ref(
            getDatabase(),
            `teachers/${teacherId}/employmentSetting`
        )

        // let data: EmploymentSetting = {
        //     locationState,
        //     state: 'incomplete',
        //     ...(type === 'earlyChildcare'
        //         ? { type: 'earlyChildcare', level: level || null }
        //         : { type }),
        // }

        // if (type === 'earlyChildcare') {
        // }
        const newId = await push(nodeRef, setting)

        return { code: 200, data: [newId.key!, setting] }
    } catch (error: any) {
        return { code: 500, error: error }
    }
}

export const removeTeacherEmploymentSetting = async (
    teacherId: string,
    settingId: string
) => {
    return remove(
        ref(
            getDatabase(),
            `teachers/${teacherId}/employmentSetting/${settingId}`
        )
    )
}

export const setTeacherEmploymentSettingState = async (
    teacherId: string,
    settingId: string,
    state: TeacherAccountState
) => {
    const nodeRef = ref(
        getDatabase(),
        `teachers/${teacherId}/employmentSetting/${settingId}`
    )
    return update(nodeRef, {
        state,
    })
}

export const updateECCAccreditationLevel = async (
    teacherId: string,
    settingId: string,
    level: ECCAccreditationLevel | null
) => {
    try {
        const nodeRef = ref(
            getDatabase(),
            `teachers/${teacherId}/employmentSetting/${settingId}`
        )
        await update(nodeRef, {
            level,
        })
        return { code: 200 }
    } catch (error: any) {
        return { code: 500, error }
    }
}

export const updateEmploymentSettingLocationStates = (
    teacherUid: string,
    locationState: string,
    settingIds: string[]
) => {
    if (settingIds.length === 0) return
    return update(
        ref(db, `teachers/${teacherUid}/employmentSetting`),
        settingIds.reduce(
            (a, c) => ({ ...a, [`${c}/locationState`]: locationState }),
            {} as Record<string, any>
        )
    )
}

export const empSettingLocaltoFb = (
    locationState: string,
    type?: LocalTeacherEmploymentType,
    state?: FieldState
) => {
    let data: any = {}

    switch (type) {
        case 'both':
            data[`${uuidv4()}`] = {
                locationState,
                type: 'school',
                state: state || FieldState.INCOMPLETE,
            }
            data[`${uuidv4()}`] = {
                locationState,
                type: 'earlyChildcare',
                state: state || FieldState.INCOMPLETE,
            }
            break
        default:
            data[`${uuidv4()}`] = {
                locationState,
                type,
                state: state || FieldState.INCOMPLETE,
            }
    }

    return data
}

export const removeIncompleteAccreditationDocs = async (teacherId: string) => {
    try {
        const db = getDatabase()
        const basePath = `teachers/${teacherId}/accreditation`
        const snap = await get(query(ref(db, basePath)))

        await Promise.all(
            Object.entries(snap.val() || {})
                .filter(
                    (doc) =>
                        ((doc[1] as AccreditationDocument).state ||
                            'incomplete') === FieldState.INCOMPLETE
                )
                .map(([key, val]) => remove(ref(db, `${basePath}/${key}`)))
        )

        return { code: 200 }
    } catch (error: any) {
        return { code: 500, error: error }
    }
}

export const updateTeacherUserDetails = async (
    userId: string,
    data: {
        firstName?: string
        lastName?: string
        firstNameActual?: string | null
        contact?: string
        email?: string
    }
) => {
    try {
        const { firstName, lastName, firstNameActual, contact, email } = data

        // Update Teachers node
        const teacherRef = ref(getDatabase(), `teachers/${userId}`)

        await update(teacherRef, {
            ...(firstName !== undefined
                ? { 'firstName/value': firstName }
                : null),
            ...(firstNameActual !== undefined
                ? { 'firstNameActual/value': firstNameActual }
                : null),
            ...(lastName !== undefined ? { 'lastName/value': lastName } : null),
            ...(contact !== undefined ? { 'contact/value': contact } : null),
            ...(email !== undefined ? { 'email/value': email } : null),
        })
        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const ES_submitHasQualification = (
    uid: string,
    settingId: string,
    hasQualification: boolean
) => {
    return update(
        ref(getDatabase(), `teachers/${uid}/employmentSetting/${settingId}`),
        {
            hasQualification,
        }
    )
}
